/**
 * @fileoverview Heat pump outdoor unit, outdoor conditions and ground line.
 *
 * @module HydraulicBalancingTesting/components/HeatPumpUnit
 */

import React from 'react';

import { SVG_DIM, COLORS, HEAT_PUMP } from '../constants';

import externalUnit from '../externalUnitHeatPump.png';
import externalUnitFan from '../externalUnitHeatPumpFan.gif';
import outdoorWeatherIcon from '../outdoorWeather.png';
import logo from '../HeatCraftHPLogo.png';

const { GROUND_Y, WIDTH } = SVG_DIM;

/** COP → colour (matches the KPI strip thresholds). */
const copColor = (cop) => (cop >= 3.5 ? COLORS.GOOD : cop >= 2.8 ? COLORS.WARM : COLORS.HOT);

/**
 * @param {Object} props
 * @param {boolean} props.isRunning
 * @param {boolean} props.hasFlow
 * @param {number} props.tSupply - [°C]
 * @param {number} props.tReturn - mixed return [°C]
 * @param {number} props.cop
 * @param {number} props.tOutdoor - [°C]
 * @param {number} props.pumpHead - [kPa]
 * @param {number} props.pumpPower - [W]
 * @param {boolean} props.minFlowAlarm
 * @param {string} props.supplyMode - 'fixed' | 'curve'
 */
export const HeatPumpUnit = React.memo(({
  isRunning,
  hasFlow,
  tSupply,
  tReturn,
  cop,
  tOutdoor,
  pumpHead,
  pumpPower,
  minFlowAlarm,
  supplyMode,
}) => {
  const fanActive = isRunning && hasFlow && !minFlowAlarm;
  const spread = tSupply - tReturn;
  const spreadGood = spread >= 4;

  // compact instrument-meter geometry (single column, kept left of the risers at x≈92)
  const cardX = 4;
  const cardW = 86;
  const cardY = GROUND_Y - 250;
  const cardH = 160;
  const labelX = cardX + 8;
  const valueX = cardX + cardW - 6;
  const row = (i) => cardY + 38 + i * 21;

  return (
    <g>
      {/* ground */}
      <rect x={10} y={GROUND_Y} width={WIDTH - 20} height={8} fill="rgba(0,0,0,0.3)" />
      <image href={logo} x={15} y={GROUND_Y + 28} width={190} />

      {/* outdoor conditions (top-left) */}
      <g>
        <image href={outdoorWeatherIcon} x={16} y={14} width={40} />
        <text x={60} y={40} fontSize={20} fontWeight={600} fill={tOutdoor < 0 ? COLORS.COLD : COLORS.GRAY}>
          {tOutdoor.toFixed(0)}°C
        </text>
        <text x={16} y={66} fontSize={10} fill={COLORS.GRAY}>
          outdoor (design −10°C)
        </text>
      </g>

      {/* heat pump unit */}
      <image href={externalUnit} width={66} x={18} y={GROUND_Y - 64} />
      {fanActive && <image href={externalUnitFan} width={32} x={23} y={GROUND_Y - 59} />}

      {/* connection pipes HP → risers */}
      <line x1={84} y1={GROUND_Y - 24} x2={92} y2={GROUND_Y - 16} stroke={COLORS.SUPPLY} strokeWidth={4} strokeLinecap="round" />
      <line x1={84} y1={GROUND_Y - 14} x2={110} y2={GROUND_Y - 13} stroke={COLORS.RETURN} strokeWidth={4} strokeLinecap="round" />

      {/* heat-pump instrument meter (Supply / Return / ΔT / COP + pump) */}
      <g>
        <rect x={cardX} y={cardY} width={cardW} height={cardH} rx={8} fill="#f7faff" stroke={COLORS.BORDER} />
        <text x={labelX} y={cardY + 16} fontSize={9.5} fontWeight={700} fill={COLORS.PRIMARY} letterSpacing="0.4">
          HEAT PUMP
        </text>
        <line x1={cardX + 6} y1={cardY + 22} x2={cardX + cardW - 6} y2={cardY + 22} stroke={COLORS.BORDER} />

        {/* Supply */}
        <text x={labelX} y={row(0)} fontSize={9} fill={COLORS.GRAY}>Supply</text>
        <text x={valueX} y={row(0)} fontSize={12} fontWeight={700} textAnchor="end" fill={COLORS.HOT}>{tSupply.toFixed(1)}°C</text>
        {/* Return */}
        <text x={labelX} y={row(1)} fontSize={9} fill={COLORS.GRAY}>Return</text>
        <text x={valueX} y={row(1)} fontSize={12} fontWeight={700} textAnchor="end" fill={COLORS.RETURN}>{tReturn.toFixed(1)}°C</text>
        {/* Spread ΔT */}
        <text x={labelX} y={row(2)} fontSize={9} fill={COLORS.GRAY}>ΔT</text>
        <text x={valueX} y={row(2)} fontSize={12} fontWeight={700} textAnchor="end" fill={spreadGood ? COLORS.GOOD : COLORS.WARM}>{spread.toFixed(1)}°C</text>
        {/* COP */}
        <text x={labelX} y={row(3)} fontSize={9} fill={COLORS.GRAY}>COP</text>
        <text x={valueX} y={row(3)} fontSize={12} fontWeight={700} textAnchor="end" fill={copColor(cop)}>{cop.toFixed(1)}</text>

        <line x1={cardX + 6} y1={row(3) + 9} x2={cardX + cardW - 6} y2={row(3) + 9} stroke={COLORS.BORDER} />
        {/* circulation pump */}
        <text x={labelX} y={row(4) + 9} fontSize={9} fill={COLORS.GRAY}>Head</text>
        <text x={valueX} y={row(4) + 9} fontSize={11} fontWeight={600} textAnchor="end" fill={COLORS.GRAY}>{pumpHead.toFixed(0)} kPa</text>
        <text x={labelX} y={row(5) + 9} fontSize={9} fill={COLORS.GRAY}>Power</text>
        <text x={valueX} y={row(5) + 9} fontSize={11} fontWeight={600} textAnchor="end" fill={COLORS.GRAY}>{pumpPower.toFixed(0)} W</text>

        <title>Heat-pump operating point — supply, return, spread (ΔT), COP and circulation pump</title>
      </g>

      {/* minimum-flow alarm (above the meter) */}
      {minFlowAlarm && (
        <g>
          <rect x={cardX} y={cardY - 34} width={cardW} height={30} rx={4} fill="#fff1f0" stroke={COLORS.HOT} />
          <text x={cardX + 8} y={cardY - 20} fontSize={10} fontWeight={700} fill={COLORS.HOT}>
            ⚠ MIN FLOW
          </text>
          <text x={cardX + 8} y={cardY - 8} fontSize={9} fill={COLORS.HOT}>
            &lt; {HEAT_PUMP.MIN_FLOW_M3H} m³/h — short-cycling
          </text>
          <title>The heat pump requires a minimum circulation flow — it is short-cycling!</title>
        </g>
      )}
    </g>
  );
});

HeatPumpUnit.displayName = 'HeatPumpUnit';
